/**
 * Whether an operation item's status is "behind schedule" relative to how
 * close the service/itinerary start is (TCO-OPS01-058 / XT-167).
 *
 * Reuses the same status×proximity matrix as the subitem-button colour so a
 * red signal means "delayed / needs attention before start".
 */
import type { SubitemKind } from './subitem-eligibility';
import { subitemButtonColor } from './subitem-button-color';

/**
 * True when the status+start combination would paint the subitem affordance
 * red — i.e. the workflow is late relative to the start date.
 *
 * Returns false when there is no start date (no "delayed vs start" without a
 * start to compare against). Pass `kind: 'ticket'` for ticket thresholds;
 * defaults to the Transport/Guide matrix for main product rows.
 */
export const isOperationStatusDelayed = (
  statusCode: string | null | undefined,
  startTime: string | null | undefined,
  kind: SubitemKind = 'transport'
): boolean => {
  if (startTime == null || String(startTime).trim() === '') return false;
  return subitemButtonColor(kind, statusCode ?? null, startTime) === 'red';
};

/** Map an operations list row to the status×proximity matrix kind. */
export const delayedStatusKindForItem = (item: {
  contentSubcategory?: string | null;
  contentCategory?: string | null;
}): SubitemKind => {
  if (item.contentSubcategory === 'Ticket') return 'ticket';
  if (item.contentSubcategory === 'Guide') return 'guide';
  if (item.contentCategory === 'Transportation') return 'transport';
  return 'transport';
};
